<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="$emit('close')"></div>

    <section
      class="app-modal__dialog invoice-concept-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invoice-concept-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="invoice-concept-modal-title">{{ isEditMode ? text.create.ui.conceptModal.titleEdit : text.create.ui.conceptModal.titleCreate }}</h3>
          <p>{{ text.create.ui.conceptModal.subtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.create.ui.conceptModal.closeAriaLabel"
          @click="$emit('close')"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <section v-if="showModeSelector" class="invoice-concept-modal__mode-selector">
        <label class="invoice-concept-modal__mode-option app-modal-toggle">
          <input
            type="checkbox"
            :checked="sourceMode === 'payment-plan'"
            @change="$emit('change-source-mode', 'payment-plan')"
          >
          <span>{{ text.create.ui.conceptModal.modeSelector.paymentPlan }}</span>
        </label>
        <label class="invoice-concept-modal__mode-option app-modal-toggle">
          <input
            type="checkbox"
            :checked="sourceMode === 'manual'"
            @change="$emit('change-source-mode', 'manual')"
          >
          <span>{{ text.create.ui.conceptModal.modeSelector.manual }}</span>
        </label>
      </section>

      <div class="app-modal__content">
        <form class="app-modal__form" @submit.prevent="submitConcept">
          <section class="app-modal__section invoice-concept-modal__section-main">
            <div class="app-modal__grid two-columns">
              <div class="app-modal-field">
                <span>{{ text.create.ui.conceptModal.fields.services }}</span>
                <multiselect
                  v-model="form.service"
                  :options="serviceOptions"
                  track-by="value"
                  label="label"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="false"
                  class="app-modal-select"
                  :placeholder="text.create.ui.conceptModal.placeholders.service"
                  :class="{ 'is-invalid': v$?.form?.service?.$error }"
                  @close="touchField('service')"
                >
                  <template #option="{ option }">
                    <div class="expense-supplier-option">
                      <strong>{{ formatServiceOptionTitle(option) }}</strong>
                      <small>{{ formatServiceOptionCode(option) }}</small>
                      <small v-if="formatServiceOptionConcept(option)">{{ formatServiceOptionConcept(option) }}</small>
                    </div>
                  </template>
                  <template #singleLabel="{ option }">
                    <div class="expense-supplier-option">
                      <span>{{ formatServiceOptionTitle(option) }}</span>
                    </div>
                  </template>
                  <template #noResult><span>{{ generalText.common.noOption }}</span></template>
                  <template #noOptions><span>{{ generalText.common.noOption }}</span></template>
                </multiselect>
                <div class="invalid-feedback d-block" v-if="v$?.form?.service?.$error">
                  <span v-if="v$.form.service.$errors.length">{{ v$.form.service.$errors[0].$message }}</span>
                </div>
              </div>

              <div class="app-modal-field">
                <span>{{ text.create.ui.conceptModal.fields.unit }}</span>
                <multiselect
                  v-model="form.unit"
                  :options="unitOptions"
                  track-by="value"
                  label="label"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="false"
                  class="app-modal-select"
                  :placeholder="text.create.ui.conceptModal.placeholders.unit"
                  :class="{ 'is-invalid': v$?.form?.unit?.$error }"
                  @close="touchField('unit')"
                />
                <div class="invalid-feedback d-block" v-if="v$?.form?.unit?.$error">
                  <span v-if="v$.form.unit.$errors.length">{{ v$.form.unit.$errors[0].$message }}</span>
                </div>
              </div>

              <label class="app-modal-field full-width">
                <span>{{ text.create.ui.conceptModal.fields.concept }}</span>
                <textarea
                  v-model.trim="form.description"
                  class="form-control app-modal-field__textarea"
                  :class="{ 'is-invalid': v$?.form?.description?.$error }"
                  rows="3"
                  :placeholder="text.create.ui.conceptModal.placeholders.concept"
                  @blur="touchField('description')"
                ></textarea>
                <div class="invalid-feedback d-block" v-if="v$?.form?.description?.$error">
                  <span v-if="v$.form.description.$errors.length">{{ v$.form.description.$errors[0].$message }}</span>
                </div>
              </label>
            </div>
          </section>

          <section class="app-modal__section invoice-concept-modal__section-amounts">
            <section class="payment-plan-pay-modal__amount-box">
              <header class="payment-plan-pay-modal__amount-box-head">
                <div>
                  <h5>{{ text.create.ui.conceptModal.amounts.title }}</h5>
                  <p>{{ text.create.ui.conceptModal.amounts.subtitle }}</p>
                </div>

                <AppCurrencyControl
                  v-model="currencyCode"
                  :exchange-rate="exchangeRate"
                  @update:exchange-rate="exchangeRate = $event"
                />
              </header>

              <div class="app-modal__grid two-columns">
                <label class="app-modal-field">
                  <span>{{ text.create.ui.conceptModal.fields.qty }}</span>
                  <input
                    v-model.number="form.qty"
                    type="number"
                    min="0.01"
                    step="0.01"
                    :disabled="isEditingMergedPaymentPlanConcept"
                    class="form-control"
                    :class="{ 'is-invalid': v$?.form?.qty?.$error }"
                    :placeholder="text.create.ui.conceptModal.placeholders.qty"
                    @blur="touchField('qty')"
                  />
                  <div class="invalid-feedback d-block" v-if="v$?.form?.qty?.$error">
                    <span v-if="v$.form.qty.$errors.length">{{ v$.form.qty.$errors[0].$message }}</span>
                  </div>
                </label>

                <label class="app-modal-field">
                  <span>{{ text.create.ui.conceptModal.fields.unitPrice }}</span>
                  <AppMoneyInput
                    v-model="form.unitPrice"
                    output-mode="number"
                    :decimal-places="2"
                    :minimum-value="0"
                    :currency="currencyCode"
                    :disabled="isEditingMergedPaymentPlanConcept"
                    @change="touchField('unitPrice')"
                  />
                  <div class="invalid-feedback d-block" v-if="v$?.form?.unitPrice?.$error">
                    <span v-if="v$.form.unitPrice.$errors.length">{{ v$.form.unitPrice.$errors[0].$message }}</span>
                  </div>
                </label>
              </div>

              <AppGeneralTaxesBlock
                :show-taxes-section="true"
                :show-tax-selectors="showTaxSelectors"
                :without-taxes-enabled="form.withoutTaxes"
                :disabled="isEditingMergedPaymentPlanConcept"
                :form="form"
                :options="taxSelectorOptions"
                :text="taxesSectionText"
                :common-text="taxesCommonText"
                @toggle-without-taxes="toggleWithoutTaxes"
              />
              <div class="invalid-feedback d-block" v-if="v$?.form?.taxSelection?.$error">
                <span v-if="v$.form.taxSelection.$errors.length">{{ v$.form.taxSelection.$errors[0].$message }}</span>
              </div>

              <div class="invoice-concept-modal__totals-wrap">
                <AppGeneralTotalsSummary
                  :tax-preview="taxPreview"
                  :show-tax-breakdown-summary="showTaxBreakdownSummary"
                  :show-tax-section-breakdown="showTaxSectionBreakdown"
                  :transferred-tax-items="transferredTaxItems"
                  :withheld-tax-items="withheldTaxItems"
                  :format-money="formatMoney"
                  :format-tax-breakdown-line="formatTaxBreakdownLine"
                />
              </div>
            </section>
          </section>

          <footer class="app-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" @click="$emit('close')">{{ generalText.common.cancel }}</button>
              <button type="submit" class="btn btn-primary">
                {{ isEditMode ? text.create.ui.conceptModal.actions.submitEdit : text.create.ui.conceptModal.actions.submitCreate }}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import AppGeneralTaxesBlock from '@/components/common/AppGeneralTaxesBlock.vue';
import AppGeneralTotalsSummary from '@/components/common/AppGeneralTotalsSummary.vue';
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import AppCurrencyControl from '@/components/common/AppCurrencyControl.vue';
import invoicesService from '@/services/invoices';
import unitsService from '@/services/units';
import taxesService from '@/services/taxes';
import {
  formatExpenseFeeMoney,
  formatExpenseFeeTaxBreakdownLine,
} from '@/composables/expensesFees/shared/useExpenseFeeTaxFlow';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { findOptionByRawValue } from '@/utils/optionValues';
import { truncateText } from '@/utils/textValue';
import { useDomainText } from '@/composables/text/useDomainText';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

const { catalog: invoiceText } = useDomainText('invoices');
const { catalog: generalText } = useDomainText('general');

const createInitialTaxPreview = () => ({
  amount: 0,
  discount: 0,
  subtotal: 0,
  calculation_type: 'from-subtotal',
  tax_type: 'individual',
  taxes: {
    transferred_total: 0,
    withheld_total: 0,
    local_transferred_total: 0,
    local_withheld_total: 0,
    net_total: 0,
    items: [],
    summary: [],
  },
  total: 0,
});

export default {
  name: 'InvoiceCreateConceptManualMode',
  components: {
    AppGeneralTaxesBlock,
    AppGeneralTotalsSummary,
    AppMoneyInput,
    AppCurrencyControl,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    showModeSelector: {
      type: Boolean,
      default: false,
    },
    sourceMode: {
      type: String,
      default: 'manual',
    },
  },
  emits: ['close', 'submit', 'change-source-mode'],
  data() {
    return {
      text: invoiceText,
      generalText,
      form: {
        service: null,
        unit: null,
        satCode: '',
        description: '',
        qty: 1,
        unitPrice: 0,
        taxType: 'individual',
        taxes: [],
        taxGroup: null,
        withoutTaxes: false,
      },
      serviceOptions: [],
      unitOptions: [],
      transferredTaxOptions: [],
      taxGroupOptions: [],
      currencyCode: String(this.currency || 'MXN').trim().toUpperCase() || 'MXN',
      exchangeRate: 1,
      catalogsLoaded: false,
      loadingCatalogs: false,
      isHydratingForm: false,
      taxPreview: createInitialTaxPreview(),
      taxPreviewLoading: false,
      taxPreviewRequestToken: 0,
      taxPreviewDebounceTimer: null,
      taxPreviewDebounceMs: 350,
      skipNextServicePreset: false,
    };
  },
  validations() {
    return {
      form: {
        service: {
          required: helpers.withMessage(
            this.text.create.ui.conceptModal.validation.serviceRequired,
            (value) => value && typeof value === 'object' && String(value.value || '').trim() !== ''
          ),
        },
        unit: {
          required: helpers.withMessage(
            this.text.create.ui.conceptModal.validation.unitRequired,
            (value) => value && typeof value === 'object' && String(value.value || '').trim() !== ''
          ),
        },
        description: {
          required: helpers.withMessage(
            this.text.create.ui.conceptModal.validation.descriptionRequired,
            (value) => String(value || '').trim() !== ''
          ),
        },
        qty: {
          validQty: helpers.withMessage(
            this.text.create.ui.conceptModal.validation.qtyInvalid,
            (value) => Number(value || 0) > 0
          ),
        },
        unitPrice: {
          validUnitPrice: helpers.withMessage(
            this.text.create.ui.conceptModal.validation.unitPriceInvalid,
            (value) => Number(value || 0) >= 0
          ),
        },
        taxSelection: {
          requiredTax: helpers.withMessage(
            this.text.create.ui.conceptModal.validation.taxSelectionRequired,
            () => this.form.withoutTaxes || !!this.selectedTaxOption
          ),
        },
      },
    };
  },
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || '').trim().toLowerCase() === 'edit';
    },
    showTaxSelectors() {
      return !this.form.withoutTaxes;
    },
    taxSelectorOptions() {
      return {
        taxModes: [
          { value: 'individual', label: this.generalText.financial.taxes.mode.individual },
          { value: 'group', label: this.generalText.financial.taxes.mode.group },
        ],
        taxes: this.transferredTaxOptions,
        taxGroups: this.taxGroupOptions,
      };
    },
    taxesSectionText() {
      return this.generalText.financial.taxes.section;
    },
    taxesCommonText() {
      return {
        noOption: this.generalText.common.noOption,
      };
    },
    selectedTaxOption() {
      if (this.form.withoutTaxes) {
        return null;
      }

      if (String(this.form.taxType || '').trim().toLowerCase() === 'group') {
        return this.form.taxGroup;
      }

      const list = Array.isArray(this.form.taxes) ? this.form.taxes : [];
      return list[0] || null;
    },
    selectedTaxLabel() {
      if (this.form.withoutTaxes) {
        return this.generalText.financial.taxes.withoutTaxes;
      }

      return String(this.selectedTaxOption?.label || '').trim() || this.generalText.financial.taxes.tax;
    },
    subtotal() {
      return (Number(this.form.qty || 0) * Number(this.form.unitPrice || 0)) || 0;
    },
    taxAmount() {
      const transferred = Number(this.taxPreview?.taxes?.transferred_total || 0);
      const withheld = Number(this.taxPreview?.taxes?.withheld_total || 0);

      return transferred - withheld;
    },
    total() {
      const backendTotal = Number(this.taxPreview?.total || 0);
      if (Number.isFinite(backendTotal) && backendTotal > 0) {
        return backendTotal;
      }

      return this.subtotal + this.taxAmount;
    },
    transferredTaxItems() {
      const items = Array.isArray(this.taxPreview?.taxes?.items) ? this.taxPreview.taxes.items : [];
      return items.filter((item) => String(item?.direction || '').trim().toLowerCase() === 'trasladado');
    },
    withheldTaxItems() {
      const items = Array.isArray(this.taxPreview?.taxes?.items) ? this.taxPreview.taxes.items : [];
      return items.filter((item) => String(item?.direction || '').trim().toLowerCase() === 'retenido');
    },
    showTaxBreakdownSummary() {
      return !this.form.withoutTaxes;
    },
    showTaxSectionBreakdown() {
      return String(this.form.taxType || '').trim().toLowerCase() === 'group';
    },
    canSubmit() {
      const hasLockedInheritedTax = this.isEditingMergedPaymentPlanConcept
        && !this.form.withoutTaxes
        && (this.initialTaxId > 0 || this.initialTaxGroupId > 0);

      return this.form.service
        && this.form.unit
        && String(this.form.description || '').trim() !== ''
        && Number(this.form.qty || 0) > 0
        && Number(this.form.unitPrice || 0) >= 0
        && (this.form.withoutTaxes || this.selectedTaxOption || hasLockedInheritedTax);
    },
    initialTaxId() {
      return Number(this.initialData?.taxId || this.initialData?.tax_id || 0) || 0;
    },
    initialTaxGroupId() {
      return Number(this.initialData?.taxGroupId || this.initialData?.tax_group_id || 0) || 0;
    },
    initialSourcePaymentIds() {
      const explicitIds = Array.isArray(this.initialData?.sourcePaymentIds)
        ? this.initialData.sourcePaymentIds
        : [];
      const parsedExplicit = explicitIds
        .map((value) => Number(value || 0) || 0)
        .filter((value) => value > 0);

      if (parsedExplicit.length > 0) {
        return [...new Set(parsedExplicit)];
      }

      const paymentId = Number(this.initialData?.paymentId || this.initialData?.payment_id || 0) || 0;
      return paymentId > 0 ? [paymentId] : [];
    },
    isEditingPaymentPlanConcept() {
      if (!this.isEditMode || !this.initialData) {
        return false;
      }

      const sourceMode = String(this.initialData?.sourceMode || '').trim().toLowerCase();
      if (sourceMode === 'payment-plan') {
        return true;
      }

      return this.initialSourcePaymentIds.length > 0;
    },
    isEditingMergedPaymentPlanConcept() {
      if (!this.isEditingPaymentPlanConcept) {
        return false;
      }

      if (Boolean(this.initialData?.isPaymentPlanMerged)) {
        return true;
      }

      if (this.initialSourcePaymentIds.length > 1) {
        return true;
      }

      const legacyMergedDescription = String(this.text.create.ui.conceptModal.paymentPlan.summary.mergedDescription || '')
        .trim()
        .toLowerCase();
      const currentDescription = String(this.initialData?.description || '').trim().toLowerCase();

      return legacyMergedDescription !== '' && currentDescription !== '' && currentDescription === legacyMergedDescription;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.openModal();
          this.resetValidationState();
        } else {
          this.clearTaxPreviewDebounce();
          this.taxPreview = createInitialTaxPreview();
          this.resetForm();
          this.resetValidationState();
        }
      },
    },
    initialData() {
      if (!this.show) {
        return;
      }

      this.hydrateFromInitialData();
    },
    'form.taxType'(nextValue) {
      if (this.isHydratingForm) {
        return;
      }

      if (this.isEditingMergedPaymentPlanConcept) {
        return;
      }

      const mode = String(nextValue || '').trim().toLowerCase();
      if (mode === 'group') {
        this.form.taxes = [];
        if (!this.form.taxGroup) {
          this.form.taxGroup = this.taxGroupOptions[0] || null;
        }
        return;
      }

      this.form.taxGroup = null;
      if (!Array.isArray(this.form.taxes) || this.form.taxes.length === 0) {
        this.form.taxes = [this.transferredTaxOptions[0] || null].filter(Boolean);
      }

      this.scheduleTaxPreview();
    },
    'form.service'(nextValue) {
      if (this.skipNextServicePreset) {
        this.skipNextServicePreset = false;
        return;
      }

      this.applyServicePreset(nextValue);
    },
    'form.qty'() {
      this.scheduleTaxPreview();
    },
    'form.unitPrice'() {
      this.scheduleTaxPreview();
    },
    'form.taxes': {
      deep: true,
      handler() {
        this.scheduleTaxPreview();
      },
    },
    'form.taxGroup'() {
      this.scheduleTaxPreview();
    },
    'form.withoutTaxes'() {
      this.scheduleTaxPreview();
    },
  },
  beforeUnmount() {
    this.clearTaxPreviewDebounce();
  },
  methods: {
    getValidationState() {
      const validation = this.v$?.value && typeof this.v$.value === 'object'
        ? this.v$.value
        : this.v$;

      return validation && typeof validation === 'object' ? validation : null;
    },
    resetValidationState() {
      const validation = this.getValidationState();
      if (typeof validation?.$reset === 'function') {
        validation.$reset();
      }
    },
    touchValidationState() {
      const validation = this.getValidationState();
      if (typeof validation?.$touch === 'function') {
        validation.$touch();
      }
    },
    hasValidationErrors() {
      const validation = this.getValidationState();
      return Boolean(validation?.$invalid);
    },
    async openModal() {
      await this.loadCatalogs();
      this.hydrateFromInitialData();
      this.scheduleTaxPreview();
    },
    clearTaxPreviewDebounce() {
      if (!this.taxPreviewDebounceTimer) {
        return;
      }

      clearTimeout(this.taxPreviewDebounceTimer);
      this.taxPreviewDebounceTimer = null;
    },
    scheduleTaxPreview() {
      if (!this.show || this.isHydratingForm) {
        return;
      }

      this.clearTaxPreviewDebounce();
      this.taxPreviewDebounceTimer = setTimeout(() => {
        this.taxPreviewDebounceTimer = null;
        this.fetchTaxPreview();
      }, this.taxPreviewDebounceMs);
    },
    buildTaxPreviewPayload() {
      const withoutTaxes = Boolean(this.form?.withoutTaxes);
      const taxMode = String(this.form?.taxType || 'individual').trim().toLowerCase() === 'group'
        ? 'group'
        : 'individual';

      const taxIds = (Array.isArray(this.form?.taxes) ? this.form.taxes : [])
        .map((item) => Number(item?.value || 0))
        .filter((id) => Number.isFinite(id) && id > 0);

      const taxGroupId = Number(this.form?.taxGroup?.value || 0);

      return {
        amount: this.subtotal,
        subtotal: this.subtotal,
        discount: 0,
        calculation_type: 'from-subtotal',
        tax_type: withoutTaxes ? 'individual' : taxMode,
        tax_ids: withoutTaxes ? [] : (taxMode === 'individual' ? taxIds : []),
        tax_group_id: withoutTaxes ? 0 : (taxMode === 'group' ? taxGroupId : 0),
        local_taxes: [],
      };
    },
    async fetchTaxPreview() {
      if (!this.form?.service) {
        this.taxPreview = createInitialTaxPreview();
        this.taxPreviewLoading = false;
        return;
      }

      const requestToken = this.taxPreviewRequestToken + 1;
      this.taxPreviewRequestToken = requestToken;
      this.taxPreviewLoading = true;

      try {
        const response = await taxesService.preview(this.buildTaxPreviewPayload(), { skipLoading: true });
        if (requestToken !== this.taxPreviewRequestToken) {
          return;
        }

        const payload = extractPayload(response);
        this.taxPreview = {
          ...createInitialTaxPreview(),
          ...(payload && typeof payload === 'object' ? payload : {}),
        };
      } catch (error) {
        if (requestToken === this.taxPreviewRequestToken) {
          this.taxPreview = createInitialTaxPreview();
        }
      } finally {
        if (requestToken === this.taxPreviewRequestToken) {
          this.taxPreviewLoading = false;
        }
      }
    },
    async loadCatalogs() {
      if (this.loadingCatalogs) {
        return;
      }

      if (this.catalogsLoaded) {
        return;
      }

      this.loadingCatalogs = true;
      try {
        const [productsResult, unitsResult, taxesResult, taxGroupsResult] = await Promise.allSettled([
          invoicesService.productSelectors(),
          unitsService.selectors(),
          taxesService.selectors(),
          taxesService.groupSelectors(),
        ]);

        if (productsResult.status === 'fulfilled') {
          const products = extractCollection(productsResult.value);
          this.serviceOptions = (Array.isArray(products) ? products : [])
            .map((item) => {
              const id = Number(item?.id || 0);
              const label = String(item?.label || '').trim();
              if (id <= 0 || label === '') {
                return null;
              }

              return {
                value: id,
                label,
                meta: item,
              };
            })
            .filter(Boolean);
        }

        if (unitsResult.status === 'fulfilled') {
          this.unitOptions = normalizeCatalogOptions(extractCollection(unitsResult.value), { useCodeInLabel: true });
        }

        if (taxesResult.status === 'fulfilled') {
          const taxes = extractCollection(taxesResult.value);
          this.transferredTaxOptions = normalizeCatalogOptions(taxes, { useCodeInLabel: false });
        }

        if (taxGroupsResult.status === 'fulfilled') {
          this.taxGroupOptions = normalizeCatalogOptions(extractCollection(taxGroupsResult.value), { useCodeInLabel: false });
        }

        if (!this.form.taxGroup && this.taxGroupOptions.length > 0) {
          this.form.taxGroup = this.taxGroupOptions[0];
        }

        if ((!Array.isArray(this.form.taxes) || this.form.taxes.length === 0) && this.transferredTaxOptions.length > 0) {
          this.form.taxes = [this.transferredTaxOptions[0]];
        }

        this.catalogsLoaded = true;
      } finally {
        this.loadingCatalogs = false;
      }
    },
    applyServicePreset(serviceOption) {
      if (this.isHydratingForm) {
        return;
      }

      const selected = serviceOption && typeof serviceOption === 'object' ? serviceOption : null;
      if (!selected) {
        return;
      }

      const meta = selected.meta || {};

      this.form.satCode = String(meta?.sat_product_code || '').trim();
      this.form.description = String(meta?.description || '').trim();

      if (!this.isEditingMergedPaymentPlanConcept) {
        this.form.unitPrice = Number(meta?.price || 0) || 0;
      }

      const currency = String(meta?.currency || '').trim().toUpperCase();
      if (currency !== '') {
        this.currencyCode = currency;
      }

      const exchangeRate = Number(meta?.exchange_rate || 0);
      if (Number.isFinite(exchangeRate) && exchangeRate > 0) {
        this.exchangeRate = exchangeRate;
      }

      const matchedUnit = findOptionByRawValue(this.unitOptions, {
        id: meta?.unit_id,
        code: meta?.unit_code,
      }, {
        optionKeys: ['id', 'code'],
        metaKeys: ['id', 'code'],
      });

      if (matchedUnit) {
        this.form.unit = matchedUnit;
      }
    },
    toggleWithoutTaxes(event) {
      if (this.isEditingMergedPaymentPlanConcept) {
        return;
      }

      const checked = Boolean(event?.target?.checked);
      this.form.withoutTaxes = checked;
      if (checked) {
        this.form.taxes = [];
        this.form.taxGroup = null;
      } else {
        const mode = String(this.form.taxType || 'individual').trim().toLowerCase();
        if (mode === 'group') {
          this.form.taxGroup = this.taxGroupOptions[0] || null;
        } else {
          this.form.taxes = [this.transferredTaxOptions[0] || null].filter(Boolean);
        }
      }
    },
    formatMoney(amount) {
      return formatExpenseFeeMoney(amount, this.currencyCode);
    },
    submitConcept() {
      this.touchValidationState();
      if (this.hasValidationErrors() || !this.canSubmit) {
        return;
      }

      const taxMode = String(this.form.taxType || 'individual').trim().toLowerCase();
      const selectedTaxId = Number(this.selectedTaxOption?.value || 0) || 0;
      const selectedTaxGroupId = Number(this.form?.taxGroup?.value || 0) || 0;
      const inheritedTaxLabel = String(this.initialData?.taxLabel || '').trim();
      const unitMeta = this.form?.unit?.meta || {};
      const unitValue = String(this.form?.unit?.value || '').trim();
      const unitLabel = String(this.form?.unit?.label || '').trim();
      const unitCodeFromLabel = unitLabel.includes(' - ')
        ? String(unitLabel.split(' - ')[0] || '').trim()
        : '';
      const unitCode = String(unitMeta?.code || unitCodeFromLabel || '').trim();
      const unitId = Number(unitMeta?.id || unitValue || 0) || 0;
      const inheritedPaymentId = Number(this.initialData?.paymentId || this.initialData?.payment_id || 0) || 0;
      const inheritedSaleId = Number(this.initialData?.saleId || this.initialData?.sale_id || 0) || 0;
      const inheritedSourceMode = String(this.initialData?.sourceMode || '').trim().toLowerCase();
      const sourceMode = inheritedSourceMode !== ''
        ? inheritedSourceMode
        : (this.initialSourcePaymentIds.length > 0 ? 'payment-plan' : '');
      const isPaymentPlanMerged = this.isEditingMergedPaymentPlanConcept
        || Boolean(this.initialData?.isPaymentPlanMerged);
      const resolvedTaxId = this.form.withoutTaxes || taxMode === 'group'
        ? 0
        : (selectedTaxId > 0 ? selectedTaxId : this.initialTaxId);
      const resolvedTaxGroupId = this.form.withoutTaxes || taxMode !== 'group'
        ? 0
        : (selectedTaxGroupId > 0 ? selectedTaxGroupId : this.initialTaxGroupId);
      const resolvedTaxLabel = this.form.withoutTaxes
        ? this.generalText.financial.taxes.withoutTaxes
        : (String(this.selectedTaxOption?.label || '').trim()
          || (this.isEditingMergedPaymentPlanConcept ? inheritedTaxLabel : '')
          || this.generalText.financial.taxes.tax);

      this.$emit('submit', {
        serviceId: Number(this.form?.service?.value || 0) || null,
        productId: Number(this.form?.service?.value || 0) || null,
        paymentId: inheritedPaymentId,
        saleId: inheritedSaleId,
        qty: Number(this.form.qty || 0),
        unit: this.form.unit?.label || '',
        unitValue: this.form.unit?.value || '',
        unitCode,
        unitId,
        code: String(this.form.satCode || '').trim(),
        description: String(this.form.description || '').trim(),
        unitPrice: Number(this.form.unitPrice || 0),
        taxLabel: resolvedTaxLabel,
        taxPercent: this.transferredTaxItems.length > 0 ? (Number(this.transferredTaxItems[0]?.rate || 0) / 100) : 0,
        taxAmount: this.taxAmount,
        taxTransferredTotal: this.transferredTaxItems.reduce((sum, item) => sum + Number(item?.amount || 0), 0),
        taxWithheldTotal: this.withheldTaxItems.reduce((sum, item) => sum + Number(item?.amount || 0), 0),
        taxId: resolvedTaxId,
        taxGroupId: resolvedTaxGroupId,
        sourceMode,
        sourcePaymentIds: this.initialSourcePaymentIds,
        isPaymentPlanMerged,
      });
    },
    hydrateFromInitialData() {
      this.isHydratingForm = true;
      if (!this.isEditMode || !this.initialData) {
        this.skipNextServicePreset = false;
        this.resetForm();
        this.isHydratingForm = false;
        return;
      }

      const concept = this.initialData || {};
      const service = findOptionByRawValue(this.serviceOptions, [
        concept?.productId,
        concept?.product_id,
        concept?.serviceId,
        concept?.service_id,
        concept?.code,
      ], {
        optionKeys: ['value'],
        metaKeys: ['id', 'sat_product_code'],
      });

      const unitValue = String(concept.unitValue || concept.unitId || concept.unit_id || '').trim();
      const unitCode = String(concept.unitCode || concept.unit_code || '').trim();
      const unitLabel = String(concept.unit || '').trim();
      const unit = this.unitOptions.find((option) => {
        const optionValue = String(option.value || '').trim();
        const optionCode = String(option?.meta?.code || '').trim();
        const optionLabel = String(option.label || '').trim();
        return optionValue === unitValue || optionCode === unitCode || optionLabel === unitLabel;
      }) || null;

      const subtotal = (Number(concept.qty || 0) * Number(concept.unitPrice || 0)) || 0;
      const taxAmount = Number(concept.taxAmount || 0) || 0;
      const conceptTaxId = Number(concept.taxId || concept.tax_id || 0) || 0;
      const conceptTaxGroupId = Number(concept.taxGroupId || concept.tax_group_id || 0) || 0;
      const taxPercent = subtotal > 0 ? Math.round(((taxAmount / subtotal) * 100) * 100) / 100 : 16;
      const resolvedTaxById = findOptionByRawValue(this.transferredTaxOptions, conceptTaxId, {
        optionKeys: ['value'],
        metaKeys: ['id'],
      });
      const resolvedTaxByPercent = this.transferredTaxOptions
        .find((option) => Number(option.value || 0) === Number(taxPercent || 0));
      const resolvedTaxGroup = findOptionByRawValue(this.taxGroupOptions, conceptTaxGroupId, {
        optionKeys: ['value'],
        metaKeys: ['id'],
      });
      const fallbackTaxOption = conceptTaxId > 0
        ? { value: conceptTaxId, label: `${this.generalText.financial.taxes.tax} #${conceptTaxId}` }
        : null;
      const fallbackTaxGroup = conceptTaxGroupId > 0
        ? { value: conceptTaxGroupId, label: `${this.generalText.financial.taxes.mode.group} #${conceptTaxGroupId}` }
        : null;
      const hasAnyTax = conceptTaxId > 0 || conceptTaxGroupId > 0 || taxAmount !== 0;
      const resolvedTaxType = conceptTaxGroupId > 0 ? 'group' : 'individual';
      const resolvedWithoutTaxes = !hasAnyTax;

      this.skipNextServicePreset = true;

      this.form = {
        service,
        unit,
        satCode: String(concept.code || service?.meta?.sat_product_code || '').trim(),
        description: String(concept.description || '').trim(),
        qty: Number(concept.qty || 1) || 1,
        unitPrice: Number(concept.unitPrice || 0) || 0,
        taxType: resolvedTaxType,
        taxes: resolvedWithoutTaxes || resolvedTaxType === 'group'
          ? []
          : [resolvedTaxById || fallbackTaxOption || resolvedTaxByPercent || this.transferredTaxOptions[0] || null].filter(Boolean),
        taxGroup: resolvedWithoutTaxes || resolvedTaxType !== 'group'
          ? null
          : (resolvedTaxGroup || fallbackTaxGroup || this.taxGroupOptions[0] || null),
        withoutTaxes: resolvedWithoutTaxes,
      };
      this.isHydratingForm = false;
    },
    resetForm() {
      this.skipNextServicePreset = false;
      this.form = {
        service: null,
        unit: null,
        satCode: '',
        description: '',
        qty: 1,
        unitPrice: 0,
        taxType: 'individual',
        taxes: [this.transferredTaxOptions[0] || null].filter(Boolean),
        taxGroup: null,
        withoutTaxes: false,
      };
    },
    touchField(fieldName) {
      const validation = this.getValidationState();
      if (!validation?.form || !validation.form[fieldName]) {
        return;
      }

      if (typeof validation.form[fieldName].$touch === 'function') {
        validation.form[fieldName].$touch();
      }
    },
    formatTaxBreakdownLine(item) {
      return formatExpenseFeeTaxBreakdownLine(item, this.currencyCode);
    },
    formatServiceOptionTitle(option) {
      const meta = option?.meta || {};
      const serviceType = String(meta?.service_type_name || this.text.create.ui.conceptModal.serviceOption.noType).trim();
      const serviceName = String(meta?.name || option?.name || '').trim() || this.text.create.ui.conceptModal.serviceOption.noName;
      return `${this.text.create.ui.conceptModal.serviceOption.titlePrefix}: ${serviceType} - ${serviceName}`;
    },
    formatServiceOptionCode(option) {
      const satCode = String(option?.meta?.sat_product_code || '').trim() || this.text.create.ui.conceptModal.serviceOption.noName;
      return `${this.text.create.ui.conceptModal.serviceOption.codePrefix}: ${satCode}`;
    },
    formatServiceOptionConcept(option) {
      const description = String(option?.meta?.description || '').trim();
      if (description === '') {
        return '';
      }

      return `${this.text.create.ui.conceptModal.serviceOption.conceptPrefix}: ${truncateText(description, 70)}`;
    },
  },
};
</script>

<style scoped>
.invoice-concept-modal__mode-selector {
  display: flex;
  gap: 18px;
  padding: 14px 22px;
  border-bottom: 1px solid #e8edf6;
}

.invoice-concept-modal__mode-option {
  width: auto;
  min-width: 210px;
  margin: 0;
}

@media (max-width: 900px) {
  .invoice-concept-modal__mode-selector {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
